import { ApiExplorerPage, apiDocsPlugin } from '@backstage/plugin-api-docs';
import { CatalogEntityPage, CatalogIndexPage, catalogPlugin } from '@backstage/plugin-catalog';
import { CatalogImportPage, catalogImportPlugin } from '@backstage/plugin-catalog-import';
import { orgPlugin } from '@backstage/plugin-org';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { SearchPage } from '@backstage/plugin-search';
import { TechDocsIndexPage, TechDocsReaderPage, techdocsPlugin } from '@backstage/plugin-techdocs';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { BatchEditorPage } from './components/batch-editor/BatchEditorPage';
import { IncidentsPage } from './components/incidents/IncidentsPage';
import { JiraBoardsRoute } from './components/jiraBoards/JiraBoardsRoute';
import { OrganizationRoute } from './components/organization/OrganizationRoute';
import { UptimeRoute } from './components/uptime/UptimeRoute';
import { jiraBoardsTabFeatureFlag, organizationTabFeatureFlag, uptimeTabFeatureFlag } from './featureFlags/uptime';
import { Navigate, Route } from 'react-router-dom';
import { apis } from './apis';
import { Root } from './components/Root';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';

import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { AlertDisplay, OAuthRequestDialog, SignInPage } from '@backstage/core-components';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { ScaffolderFieldExtensions } from '@backstage/plugin-scaffolder-react';
import CssBaseline from '@material-ui/core/CssBaseline';
import { GcpProjectFieldExtension } from './components/scaffolder/extensions/GCPProjectField';
import { GcpServiceAccountFieldExtension } from './components/scaffolder/extensions/GCPServiceAccountField';
import { GHUsernameFieldExtension } from './components/scaffolder/extensions/githubUsernameField';
import { PrefilledRepoUrlPickerExtension } from './components/scaffolder/extensions/PrefilledRepoUrlPicker';

import { configApiRef, googleAuthApiRef, SignInPageProps, useApi } from '@backstage/core-plugin-api';
import { SoundcheckRoutingPage } from '@spotify/backstage-plugin-soundcheck';
import { Mermaid } from 'backstage-plugin-techdocs-addon-mermaid';
import { EnvsFieldExtension } from './components/scaffolder/extensions/EnvsField';
import { TemplateListPageWithDocsBanner } from './components/create/TemplateListPageWithDocsBanner';
import { batchEditorUsePermission } from './permissions/batchEditor';

const AppSignInPage = (props: SignInPageProps) => {
  const configApi = useApi(configApiRef);
  const authEnvironment = configApi.getOptionalString('auth.environment');
  const guestSignInEnabled =
    authEnvironment === 'development' &&
    configApi.getOptionalBoolean('auth.guestSignIn.enabled') === true;

  if (!guestSignInEnabled) {
    return (
      <SignInPage
        {...props}
        auto
        provider={{
          id: 'google-auth-provider',
          title: 'Google',
          message: 'Sign in using Google',
          apiRef: googleAuthApiRef,
        }}
      />
    );
  }

  return <SignInPage {...props} providers={['guest']} />;
};

const app = createApp({
  apis,
  featureFlags: [uptimeTabFeatureFlag, jiraBoardsTabFeatureFlag, organizationTabFeatureFlag],
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  components: {
    SignInPage: AppSignInPage,
  },
});

const routes = (
  <FlatRoutes>
    <Route path="/" element={<Navigate to="catalog" />} />
    <Route path="/catalog" element={<CatalogIndexPage />} />
    <Route path="/catalog/:namespace/:kind/:name" element={<CatalogEntityPage />}>
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />} />
    <Route path="/docs/:namespace/:kind/:name/*" element={<TechDocsReaderPage />}>
      <TechDocsAddons>
        <ReportIssue />
        <Mermaid config={{ theme: 'forest', themeVariables: { lineColor: '#000000' } }} />
      </TechDocsAddons>
    </Route>
    <Route
      path="/create"
      element={
        <ScaffolderPage
          components={{ EXPERIMENTAL_TemplateListPageComponent: TemplateListPageWithDocsBanner }}
        />
      }
    >
      <ScaffolderFieldExtensions>
        <EnvsFieldExtension />
        <GcpProjectFieldExtension />
        <GcpServiceAccountFieldExtension />
        <GHUsernameFieldExtension />
        <PrefilledRepoUrlPickerExtension />
      </ScaffolderFieldExtensions>
    </Route>
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route path="/soundcheck" element={<SoundcheckRoutingPage title="Health Checks" />} />
    <Route path="/jira-boards" element={<JiraBoardsRoute />} />
    <Route path="/organization" element={<OrganizationRoute />} />
    <Route path="/uptime" element={<UptimeRoute />} />
    <Route path="/incidents" element={<IncidentsPage />} />
    <Route
      path="/batch-editor"
      element={
        <RequirePermission permission={batchEditorUsePermission}>
          <BatchEditorPage />
        </RequirePermission>
      }
    />
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <AlertDisplay />
    <CssBaseline />
    <OAuthRequestDialog />
    <AppRouter>
      <Root>{routes}</Root>
    </AppRouter>
  </>,
);
